/* Idempotency-Key generation + replay-safe key cache.
 *
 * Used for any non-idempotent server action: place order, create booking,
 * create payment intent, cancel booking. The same logical action
 * (same payload hash) within the TTL replays the same key, so accidental
 * double-clicks dedupe server-side instead of double-charging.
 */

const STORE = new Map();              // payloadHash -> { key, expiresAt }
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

export function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function hashPayload(payload) {
  // FNV-1a 32-bit over JSON.stringify — enough for client-side dedup.
  const s = JSON.stringify(payload || {});
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

/**
 * Returns a stable idempotency key for a (scope, payload) pair within the TTL.
 * Pass `null` payload to force a fresh key every call.
 */
export function keyFor(scope, payload, ttlMs = DEFAULT_TTL_MS) {
  if (payload === null) return uuid();
  const hash = `${scope}:${hashPayload(payload)}`;
  const now = Date.now();
  const cached = STORE.get(hash);
  if (cached && cached.expiresAt > now) return cached.key;
  const key = uuid();
  STORE.set(hash, { key, expiresAt: now + ttlMs });
  return key;
}

/** Invalidate the key for a (scope, payload) — call after a confirmed server failure
 *  the user explicitly retries with the same payload. */
export function invalidate(scope, payload) {
  STORE.delete(`${scope}:${hashPayload(payload)}`);
}
